import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { NgForm } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { Area } from '../../shared/models/area.interface';
import { QuoteArea } from '../../shared/models/quote.area.interface';
import { QuoteAreaItem } from '../../shared/models/quote.area.item.interface';
import { QuoteAreaAccessory } from '../../shared/models/quote.area.accessory.interface';
import { ItemCoverageType, ItemType } from '../../shared/models/item.type.interface';
import { RValue } from '../../shared/models/r.value.interface';
import { JobService } from '../services/job.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ItemUsage } from '../../shared/models/item.usage.interface';
import { QuoteService } from '../../quote/services/quote.service';
import { ItemArea } from '../../shared/models/item.area.interface';
import { ChangeOrderItem } from '../../shared/models/change.order.item.interface';
import { ChangeOrderItemAccessory } from '../../shared/models/change.order.item.accessory.interface';
import { ChangeOrderItemSummary } from '../models/change.order.item.summary';
import { ItemMaster } from '../../shared/models/item.master.interface';
import { RolePermission } from '../../shared/models/role.permission.enum';
import { QuoteItemNoteTemplate } from '../../shared/models/quote.item.note.template.interface';

export interface ChangeOrderAreaDialogData {
  changeOrderId: number;
  quoteAreaId: number;
  areas: Area[];
  areaId: number;
  changeOrderItemId: number;
  quoteItemNoteTemplates: QuoteItemNoteTemplate[];
}

@Component({
  selector: 'app-change-order-area',
  templateUrl: './change.order.area.dialog.html',
  styleUrls: ['./change.order.area.dialog.scss']
})
export class ChangeOrderAreaDialog extends AbstractBiditComponent implements OnInit {
  private areaObservable$ = new Observable<Area>();
  private areaSubscription;

  productFilterItemType_ID: number;
  productFilterRValue: number;
  productUsage: boolean = false;
  productAeroBarrier: boolean = false;
  showTemplates: boolean = false;

  selectedTemplate: number = null;
  
  changeOrderSummary: ChangeOrderItemSummary = {
    quoteArea: {
      acceptedItem: {}
    },
    changeOrderItem: {
      accessories: []
    }
  } as ChangeOrderItemSummary;

  areas: Area[];
  accessories: QuoteAreaAccessory[];
  itemTypes: ItemType[];

  productRValues: RValue[];
  productItems: ItemMaster[];
  productUsages: ItemUsage[];
  showLinePricing: boolean = false;

  constructor(private jobService: JobService, private quoteService: QuoteService,
    public dialogRef: MatDialogRef<ChangeOrderAreaDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeOrderAreaDialogData, deviceDetectorService: DeviceDetectorService) {
    super(deviceDetectorService);
  }

  @Output() updated = new EventEmitter<ChangeOrderItemSummary>();
  @Output() canceled = new EventEmitter<ChangeOrderItemSummary>();

  ngOnInit() {
    this.areas = this.data.areas;
    this.showLinePricing = QuoteService.hasPermission(RolePermission.ShowLineItemPricing);

    this.loadChangeOrderItem(this.data.changeOrderId, this.data.quoteAreaId, this.data.areaId, this.data.changeOrderItemId);

    // wait until quote loaded before loading further info
    this.areaSubscription = this.areaObservable$.subscribe(area => {
      if (area == null) {
        this.changeOrderSummary.area = {} as Area;
        this.changeOrderSummary.changeOrderItem.area_ID = null;
      } else {
        this.changeOrderSummary.area = area;
        this.changeOrderSummary.changeOrderItem.area_ID = area.area_ID;

        this.mergeAccessories(area.itemAreas);

        this.refreshProductItems();
      }
    });

    this.quoteService.getItemTypes()
      .subscribe({
        next: (data) => {
          this.itemTypes = data.filter(it => !it.itemType_IsAccessory && it.itemType_IsActive);
        }
      }); 
  }

  loadChangeOrderItem(changeOrderId: number, quoteAreaId: number, areaId: number, changeOrderItemId: number) {
    this.changeOrderSummary.changeOrderItem.changeOrder_ID = changeOrderId;
    this.changeOrderSummary.changeOrderItem.changeOrderItem_IsMaterialOnly = false;

    if (quoteAreaId === null && areaId === null) return;

    this.jobService.getChangeOrderItemSummary(changeOrderId, quoteAreaId, areaId, changeOrderItemId)
      .subscribe({
        next: (data) => {
          this.changeOrderSummary = data;

          let product = this.changeOrderSummary.item;
          if (product) {
            let productType = product.itemType;
            this.productFilterItemType_ID = product.itemType_ID;
            this.productFilterRValue = product.rValue_ID;

            this.productUsages = product.itemUsages;
            this.productUsage = productType.itemCoverageType_ID != null && productType.itemCoverageType_ID != ItemCoverageType.AeroBarrier;
            this.productAeroBarrier = productType.itemCoverageType_ID != null && productType.itemCoverageType_ID == ItemCoverageType.AeroBarrier;
          } else {
            this.changeOrderSummary.changeOrderItem.changeOrderItem_IsCustomItem = data.quoteArea.acceptedItem.area_IsCustomItem;
            this.changeOrderSummary.changeOrderItem.changeOrderItem_Name = data.quoteArea.acceptedItem.area_Name;
            this.changeOrderSummary.changeOrderItem.changeOrderItem_Note = data.quoteArea.acceptedItem.area_Note;
          }

          // race condition with fetching areas
          if (this.areas != null) {
            let area = this.areas.find(a => a.area_ID == this.changeOrderSummary.area_ID);
            if (!area) return;
            area.subAreas?.sort((a, b) => a.subArea_Name > b.subArea_Name ? 1 : -1)
            this.areaSubscription.next(area);
          }
        },
        error: (error: string) => this.onError(error)
      });
  }

  onAreaChange(areaId: Number) {
    let area = this.areas.find(c => c.area_ID == areaId);
    area?.subAreas?.sort((a, b) => a.subArea_Name > b.subArea_Name ? 1 : -1)
    this.areaSubscription.next(area);
  }

  itemNoteTemplateChange(quoteItemTemplateID: number) {
    let itemNoteTemplate = this.data.quoteItemNoteTemplates.find(c => c.quoteItemNoteTemplate_ID == quoteItemTemplateID);
    this.changeOrderSummary.changeOrderItem.changeOrderItem_CustomNotes = itemNoteTemplate?.quoteItemNoteTemplate_Desc;
  }

  refreshProductItemType() {
    this.productFilterRValue = null;
    let itemType = this.itemTypes.find(it => it.itemType_ID == this.productFilterItemType_ID);
    this.productUsage = (itemType != null && (itemType.itemCoverageType_ID != null && itemType.itemCoverageType_ID != ItemCoverageType.AeroBarrier));
    this.productAeroBarrier = (itemType != null && (itemType.itemCoverageType_ID != null && itemType.itemCoverageType_ID == ItemCoverageType.AeroBarrier));

    this.refreshProductItems();
  }

  refreshProductItems() {
    this.quoteService.getRValues(this.productFilterItemType_ID)
      .subscribe({
        next: (data) => {
          this.productRValues = data;
        },
        error: (error: string) => this.onError(error)
      });

    if (this.productFilterItemType_ID == null) {
      this.productItems = null;
      return;
    }

    this.quoteService.getItems(this.productFilterItemType_ID, this.productFilterRValue)
      .subscribe({
        next: (data) => {
          this.productItems = data;
        },
        error: (error: string) => this.onError(error)
      });
  };

  refreshProductUsages() {
    this.quoteService.getItemUsages(this.changeOrderSummary.changeOrderItem.item_ID)
      .subscribe({
        next: (data) => {
          this.productUsages = data;
        },
        error: (error: string) => this.onError(error)
      });
  };

  useTemplateChange(selected: boolean) {
    this.showTemplates = selected;
  }
  ItemNoteTemplateChange(quoteItemTemplateID: number) {
    let itemNoteTemplate = this.data.quoteItemNoteTemplates.find(c => c.quoteItemNoteTemplate_ID == quoteItemTemplateID);
    this.changeOrderSummary.changeOrderItem.changeOrderItem_CustomNotes = itemNoteTemplate?.quoteItemNoteTemplate_Desc;
  }

  mergeAccessories(itemAreas: ItemArea[]) {
    this.changeOrderSummary.changeOrderItem.accessories = this.changeOrderSummary.changeOrderItem.accessories.filter(acc =>
      acc.accessory_Quantity != null && acc.accessory_Quantity != 0);

    for (let itemArea of itemAreas) {
      //if (!itemArea.item.itemType?.itemType_IsAccessory) continue;

      let accessory = this.changeOrderSummary.changeOrderItem.accessories.find((acc) => {
        return acc.item_ID == itemArea.item_ID
      });

      if (accessory == null) {
        let changeAccessory = {
          item: itemArea.item,
          item_ID: itemArea.item_ID,
          accessory_Cost: itemArea.item.item_Cost
        } as ChangeOrderItemAccessory;
        this.changeOrderSummary.changeOrderItem.accessories.push(changeAccessory);
      }
    }
  }

  saveArea(form: NgForm) {
    this.errors = '';

    if (form.valid) {
      this.isRequesting = true;

      this.jobService.saveChangeOrderItem(this.changeOrderSummary.changeOrderItem)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe({
          next:(result) => {
            if (result) {
              this.alertComplete(true);
              form.resetForm();
              this.dialogRef.close(result);
            }
          },
          error: (error: string) => this.onError(error)});
    }
  }

  cancelEdit() {
    this.alertComplete(false);
    this.dialogRef.close();
  }

  removeCustomAccessory(accessory: ChangeOrderItemAccessory) {
    let index = this.changeOrderSummary.changeOrderItem.accessories.indexOf(accessory);
    this.changeOrderSummary.changeOrderItem.accessories.splice(index, 1);
  }

  addCustomAccessory() {
    let accessory = {} as ChangeOrderItemAccessory;
    this.changeOrderSummary.changeOrderItem.accessories.push(accessory);
  }

  alertComplete(success: boolean) {
    if (success)
      this.updated.emit(this.changeOrderSummary);
    else
      this.canceled.emit(this.changeOrderSummary);

    this.changeOrderSummary = {
      changeOrderItem: {
        accessories: []
      }
    } as ChangeOrderItemSummary;

    this.productFilterItemType_ID = null;
    this.productFilterRValue = null;
  }

  ngOnDestory() {
    this.areaSubscription.unsubscribe();
  }

}
